/**
 * Phase 2.5 — Telecom Applicability & Weighting Blueprint
 * ────────────────────────────────────────────────────────
 * Informational / governance-only registry. Defines which Infrastructure
 * Security modules (C1–C8) and telecom controls are *applicable* to each
 * telecom service model.
 *
 * READ-ONLY. Does NOT affect:
 *   • calculate_csv_domain_scores
 *   • pm_recompute_assessment_scores
 *   • assessment_csv_domain_scores
 *   • reviewer scoring / NIST scoring / report scoring
 *   • score_basis_version (still v1_flat)
 *   • v2_hybrid activation
 *
 * Future scoring engines may consult this matrix; current engines must not.
 */

import { INFRASTRUCTURE_CONTROL_METADATA } from "@/configs/controlGuidance/infrastructureControlMetadata";
import { toBlueprintKey } from "@/lib/serviceModels";

export type TelecomServiceModel =
  | "MNO"
  | "MVNO"
  | "ISP"
  | "FiberCo"
  | "TowerCo"
  | "Wholesale Carrier"
  | "Private Network Operator"
  | "OTT Provider"
  | "Hyperscaler"
  | "Neutral Host"
  | "IoT / M2M Operator";

export const TELECOM_SERVICE_MODELS: TelecomServiceModel[] = [
  "MNO",
  "MVNO",
  "ISP",
  "FiberCo",
  "TowerCo",
  "Wholesale Carrier",
  "Private Network Operator",
  "OTT Provider",
  "Hyperscaler",
  "Neutral Host",
  "IoT / M2M Operator",
];

export type TelecomModuleId = "C1" | "C2" | "C3" | "C4" | "C5" | "C6" | "C7" | "C8";

export const TELECOM_MODULES: { id: TelecomModuleId; label: string }[] = [
  { id: "C1", label: "Radio Access Network" },
  { id: "C2", label: "Core Network" },
  { id: "C3", label: "Signaling Network" },
  { id: "C4", label: "Transport / Backhaul" },
  { id: "C5", label: "Virtualization & Edge" },
  { id: "C6", label: "OSS / BSS" },
  { id: "C7", label: "Roaming / Subscriber / IoT" },
  { id: "C8", label: "Service Continuity" },
];

/**
 * Service-model → applicable modules. Modules not listed are considered
 * *not applicable* for that service model (future v2_hybrid will exclude
 * them from the in-scope average).
 */
export const SERVICE_MODEL_MODULE_APPLICABILITY: Record<TelecomServiceModel, TelecomModuleId[]> = {
  "MNO":                       ["C1", "C2", "C3", "C4", "C5", "C6", "C7", "C8"],
  "MVNO":                      ["C6", "C7", "C8"],
  "ISP":                       ["C4", "C5", "C6", "C7", "C8"],
  "FiberCo":                   ["C4", "C8"],
  "TowerCo":                   ["C1", "C8"],
  "Wholesale Carrier":         ["C3", "C4", "C7", "C8"],
  "Private Network Operator":  ["C1", "C2", "C4", "C5", "C8"],
  "OTT Provider":              ["C5", "C6", "C8"],
  "Hyperscaler":               ["C5", "C8"],
  "Neutral Host":              ["C1", "C4", "C8"],
  "IoT / M2M Operator":        ["C2", "C5", "C7", "C8"],
};

/**
 * Phase 1 (Service Model Foundation) — canonical stored value → blueprint key.
 *
 * Stored/UI values are canonical (e.g. "Hyperscaler / TechCo"); this blueprint
 * historically uses the short key ("Hyperscaler"). Values are mapped, never
 * rewritten in the database. Returns null for values this blueprint does not
 * model (e.g. "Other").
 */
export function toBlueprintServiceModel(stored: string): TelecomServiceModel | null {
  const mapped = toBlueprintKey(stored);
  return (TELECOM_SERVICE_MODELS as string[]).includes(mapped)
    ? (mapped as TelecomServiceModel)
    : null;
}

export function excludedModulesFor(sm: TelecomServiceModel): TelecomModuleId[] {
  const inc = new Set(SERVICE_MODEL_MODULE_APPLICABILITY[sm]);
  return TELECOM_MODULES.map((m) => m.id).filter((id) => !inc.has(id));
}


/**
 * Control-level applicability registry.
 *
 * Derived from the module applicability matrix using each control's
 * `module` field in INFRASTRUCTURE_CONTROL_METADATA. A control is applicable
 * to a service model iff its module is applicable to that service model.
 *
 * This guarantees every telecom control has applicability metadata without
 * duplicating mappings.
 */
export interface ControlApplicabilityEntry {
  controlKey: string;
  module: string;
  applicableServiceModels: TelecomServiceModel[];
}

function deriveControlApplicability(): Record<string, ControlApplicabilityEntry> {
  const out: Record<string, ControlApplicabilityEntry> = {};
  for (const meta of Object.values(INFRASTRUCTURE_CONTROL_METADATA)) {
    // Only telecom-specific modules (C1–C8) are part of this registry.
    if (!meta.module.startsWith("C")) continue;
    const mod = meta.module as TelecomModuleId;
    const applicable = TELECOM_SERVICE_MODELS.filter((sm) =>
      SERVICE_MODEL_MODULE_APPLICABILITY[sm].includes(mod),
    );
    out[meta.controlKey] = {
      controlKey: meta.controlKey,
      module: mod,
      applicableServiceModels: applicable,
    };
  }
  return out;
}

export const TELECOM_CONTROL_APPLICABILITY: Record<string, ControlApplicabilityEntry> =
  deriveControlApplicability();

export function applicableControlsFor(sm: TelecomServiceModel): string[] {
  return Object.values(TELECOM_CONTROL_APPLICABILITY)
    .filter((c) => c.applicableServiceModels.includes(sm))
    .map((c) => c.controlKey);
}

export function excludedControlsFor(sm: TelecomServiceModel): string[] {
  return Object.values(TELECOM_CONTROL_APPLICABILITY)
    .filter((c) => !c.applicableServiceModels.includes(sm))
    .map((c) => c.controlKey);
}
